import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  REQUEST_KINDS,
  REQUEST_KIND_MAP,
  REQUEST_STATUSES,
  REQUEST_STATUS_MAP,
  type RequestKind,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, money, num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

export interface RequestRow {
  id: string;
  status: keyof typeof REQUEST_STATUS_MAP;
  statusLabel: string;
  statusKind: string;
  open: boolean;
  typeId: string;
  typeLabel: string;
  kind: string;
  kindLabel: string;
  kindTone: string;
  approverRole: string;
  onDate: string | null;
  toDate: string | null;
  fromTime: string | null;
  toTime: string | null;
  hours: number | null;
  amount: number | null;
  currency: string;
  reason: string | null;
  attachmentId: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  reimbursed: boolean;
  createdAt: string;
  employee: { id: string; name: string; code: string | null } | null;
}

export interface RequestTypeRow {
  id: string;
  key: string | null;
  label: string;
  kind: RequestKind;
  kindLabel: string;
  kindTone: string;
  description: string | null;
  needsDates: boolean;
  needsHours: boolean;
  needsAmount: boolean;
  needsReason: boolean;
  needsAttachment: boolean;
  maxPerMonth: number | null;
  maxHours: number | null;
  minNoticeDays: number | null;
  approverRole: string;
  isActive: boolean;
  order: number;
}

/**
 * طلبات الموظّفين — شاشةُ من يبتّ.
 *
 * المعلَّقُ أوّلًا لأنّه العملُ المنتظَر: من يفتح هذه الشاشة يريد أن يبتّ
 * لا أن يقرأ تاريخًا. والمصروفُ الموافَق عليه ولم يُردَّ يظهر بعلامةٍ —
 * الموافقةُ إقرارٌ بصحّة المصروف، والردُّ إخراجُ مال، وبينهما مالٌ لم يخرج.
 */
export function HrRequests() {
  const { atLeast } = useAuth();
  const [status, setStatus] = useState('PENDING');
  const [typeId, setTypeId] = useState('');
  const [deciding, setDeciding] = useState<RequestRow | null>(null);
  const [paying, setPaying] = useState<RequestRow | null>(null);

  const query = { status: status || undefined, typeId: typeId || undefined };

  const list = useQuery({
    queryKey: ['hr', 'requests', query],
    queryFn: () => api<RequestRow[]>(`/hr/requests${qs(query)}`),
  });

  const types = useQuery({
    queryKey: ['hr', 'request-types'],
    queryFn: () => api<RequestTypeRow[]>('/hr/request-types'),
  });

  const pending = useQuery({
    queryKey: ['hr', 'requests', { status: 'PENDING' }],
    queryFn: () => api<RequestRow[]>('/hr/requests?status=PENDING'),
  });

  const rows = list.data ?? [];
  const owed = (pending.data ?? []).length;
  const unpaid = rows.filter(
    (r) => r.kind === 'EXPENSE' && r.status === 'APPROVED' && !r.reimbursed,
  );

  return (
    <div className="page">
      <PageHead
        title="طلبات الموظّفين"
        subtitle="الأذونات والمصروفات والخطابات — والإجازات لها شاشتها"
        actions={
          atLeast('ADMIN') && (
            <a className="btn" href="/settings/request-types">
              <Icon name="settings" size={15} />
              قواعد الطلبات
            </a>
          )
        }
      />

      <div className="stats">
        <Stat label="ينتظر قرارك" value={num(owed)} />
        <Stat
          label="مصروفات لم تُردّ"
          value={num(unpaid.length)}
          note={
            unpaid.length > 0
              ? money(
                  unpaid.reduce((s, r) => s + (r.amount ?? 0), 0),
                  unpaid[0]!.currency,
                )
              : undefined
          }
        />
      </div>

      <div className="toolbar">
        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {REQUEST_STATUSES.map((s) => (
            <option key={s} value={s}>
              {REQUEST_STATUS_MAP[s].label}
            </option>
          ))}
        </select>

        <select
          className="select"
          value={typeId}
          onChange={(e) => setTypeId(e.target.value)}
        >
          <option value="">كل الأنواع</option>
          {(types.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {/*
        الإجازةُ ليست هنا ويُقال لماذا: من يبحث عنها في هذه الشاشة يجب أن
        يجد الطريق لا فراغًا يظنّه عطبًا.
      */}
      <Notice title="الإجازات في شاشتها" icon="calendar">
        الإجازةُ وحدَها ما يخصم رصيدًا ويدخل حسابَ المرتّب، فلها محرّكُها
        وشاشتُها في «<a className="link" href="/hr/leaves">الإجازات</a>».
        وهذه الشاشةُ لما سواها.
      </Notice>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title={status === 'PENDING' ? 'لا طلبات تنتظر قرارك' : 'لا طلبات'}
          text="الطلبات تصل من الموظّفين من شاشة «طلباتي»."
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>الموظّف</th>
                  <th>الطلب</th>
                  <th>التفاصيل</th>
                  <th>قُدّم</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className="cell-strong">
                        {r.employee?.name ?? '—'}
                      </span>
                      {r.employee?.code && (
                        <div className="tiny mute num">{r.employee.code}</div>
                      )}
                    </td>
                    <td>
                      <span>{r.typeLabel}</span>
                      <div className="tiny mute">{r.kindLabel}</div>
                    </td>
                    <td className="cell-mute">
                      <RequestFacts r={r} />
                    </td>
                    <td className="num cell-mute">{date(r.createdAt)}</td>
                    <td>
                      <Badge kind={r.statusKind}>{r.statusLabel}</Badge>
                      {r.kind === 'EXPENSE' && r.status === 'APPROVED' && (
                        <div className="tiny mute">
                          {r.reimbursed ? 'رُدّ في المرتّب' : 'لم يُردّ بعد'}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {r.status === 'PENDING' && (
                          <button
                            className="btn btn-sm"
                            onClick={() => setDeciding(r)}
                          >
                            ابتّ
                          </button>
                        )}
                        {atLeast('ADMIN') &&
                          r.kind === 'EXPENSE' &&
                          r.status === 'APPROVED' &&
                          !r.reimbursed && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setPaying(r)}
                            >
                              ردّ في المرتّب
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {deciding && (
        <DecideDialog request={deciding} onClose={() => setDeciding(null)} />
      )}
      {paying && (
        <ReimburseDialog request={paying} onClose={() => setPaying(null)} />
      )}
    </div>
  );
}

/** تفاصيلُ الطلب بحسب نوعه — لا خاناتٌ فارغةٌ لما لا يعنيه. */
export function RequestFacts({ r }: { r: RequestRow }) {
  const bits: string[] = [];
  if (r.onDate) {
    bits.push(r.toDate && r.toDate !== r.onDate ? `${date(r.onDate)} — ${date(r.toDate)}` : date(r.onDate));
  }
  if (r.fromTime && r.toTime) bits.push(`${r.fromTime} — ${r.toTime}`);
  if (r.hours) bits.push(`${r.hours} ساعة`);
  if (r.amount) bits.push(money(r.amount, r.currency));

  return (
    <>
      {bits.length > 0 && <div className="num">{bits.join(' · ')}</div>}
      {r.reason && <div className="tiny">{r.reason}</div>}
      {r.decisionNote && (
        <div className="tiny mute">القرار: {r.decisionNote}</div>
      )}
    </>
  );
}

function DecideDialog({
  request,
  onClose,
}: {
  request: RequestRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');

  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      api(`/hr/requests/${request.id}/decide`, {
        method: 'POST',
        body: { approve, note: note.trim() || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast('سُجّل القرار');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التسجيل'),
  });

  return (
    <Modal
      title={`${request.typeLabel} — ${request.employee?.name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => decide.mutate(false)}
            disabled={!note.trim() || decide.isPending}
          >
            ارفض
          </button>
          <button
            className="btn btn-primary"
            onClick={() => decide.mutate(true)}
            disabled={decide.isPending}
          >
            وافق
          </button>
        </>
      }
    >
      <div className="rc-offer-facts">
        <RequestFacts r={request} />
      </div>

      <div className="field mt-4">
        <label className="label" htmlFor="rq-note">
          ملاحظة
        </label>
        <textarea
          id="rq-note"
          className="input"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="تُقرأ عند صاحب الطلب."
        />
        <span className="tiny mute">
          الرفضُ يحتاج سببًا: من رُفض طلبُه بلا سببٍ يسأل مديرَه في الممرّ.
        </span>
      </div>
    </Modal>
  );
}

/**
 * ردُّ المصروف في مرتّب شهر.
 *
 * الشهرُ يُختار ولا يُفترض: مصروفٌ وُوفق عليه في آخر الشهر بعد إقفال
 * دورته يُردّ في التي بعدها.
 */
function ReimburseDialog({
  request,
  onClose,
}: {
  request: RequestRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });

  const pay = useMutation({
    mutationFn: () =>
      api(`/hr/requests/${request.id}/reimburse`, {
        method: 'POST',
        body: { period },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast('أُضيف البند إلى مرتّب الشهر');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الردّ'),
  });

  return (
    <Modal
      title={`ردّ ${money(request.amount ?? 0, request.currency)}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => pay.mutate()}
            disabled={pay.isPending}
          >
            {pay.isPending ? 'جارٍ الإضافة' : 'أضِف إلى المرتّب'}
          </button>
        </>
      }
    >
      <p className="tiny mute">
        يُكتب بندُ «ردّ مصروفات» في مرتّب الشهر المختار باسم{' '}
        {request.employee?.name}. وهو مالُه يعود إليه لا مكافأةً — فلا يُحسب
        في متوسّط أجره.
      </p>

      <div className="field mt-4">
        <label className="label req" htmlFor="rb-period">
          شهر الاستحقاق
        </label>
        <input
          id="rb-period"
          className="input num"
          type="month"
          value={period.slice(0, 7)}
          onChange={(e) => setPeriod(`${e.target.value}-01`)}
        />
        <span className="tiny mute">
          دورةٌ اعتُمدت لا يدخلها بندٌ جديد — اخترْ شهرًا لم يُقفل.
        </span>
      </div>
    </Modal>
  );
}

export { REQUEST_KINDS, REQUEST_KIND_MAP };
